import { useEffect, useState, type ReactNode } from 'react';
import { Check, ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  alternarEnVarias,
  atajosDeFecha,
  chipsActivos,
  listaDe,
  diaLocal,
  fechaCorta,
  type Faceta,
  type FacetaDeFechas,
  type FacetaDeOpciones,
  type ValoresDeFacetas,
} from '@/lib/superfiltro';

/**
 * SUPERFILTRO — una sola barra por lista (reunión con Josué, 14-oct-2026, punto 9:
 * «superfiltros en las listas, por industria, estado…, no botones regados»).
 *
 * Buscar + un desplegable por faceta (estado, categoría, canal, responsable,
 * fechas…), que se combinan entre sí; debajo, cuántos resultados hay, un chip por
 * filtro puesto con su «x» y «Limpiar». Los valores no viven aquí: la pantalla los
 * guarda en la URL (`useFiltrosEnUrl`), así sobreviven al recargar y un enlace abre
 * con los mismos filtros.
 *
 * En el teléfono los desplegables no caben: un solo botón «Filtros» (con cuántos
 * hay puestos) abre una hoja desde abajo con todas las facetas. `compacta` hace lo
 * mismo en escritorio, para columnas angostas como la lista de chats.
 *
 * `onCambiar` recibe TODOS los cambios de un gesto juntos (un rango de fechas son
 * dos claves; «Limpiar», todas): con `useFiltrosEnUrl` dos escrituras seguidas se
 * pisan, una sola no.
 */
export interface SuperfiltroProps {
  buscar?: string;
  onBuscar?: (texto: string) => void;
  placeholder?: string;
  facetas: Faceta[];
  valores: ValoresDeFacetas;
  onCambiar: (cambios: ValoresDeFacetas) => void;
  /** Deja todo en blanco (facetas y búsqueda) en una sola escritura. */
  onLimpiar?: () => void;
  /** El contador ya dicho: «12 de 40 ventas». */
  resultados?: string;
  /** Botones de la pantalla (exportar, columnas…): van al final de la barra. */
  acciones?: ReactNode;
  /** Chips de filtros que no son facetas (se quitan con lo suyo). */
  chipsExtra?: Array<{ clave: string; etiqueta: string; onQuitar: () => void }>;
  compacta?: boolean;
  /** Para las pruebas: lo que arranca abierto (la clave de una faceta, o `'panel'`). */
  abiertaDeEntrada?: string | null;
}

const FOCO = 'focus-visible:outline-none focus-visible:ring-[3.5px] focus-visible:ring-acento/50';
const ICONO = { strokeWidth: 1.75, 'aria-hidden': true } as const;
const PANEL = 'panel';

function etiquetaPuesta(f: Faceta, valores: ValoresDeFacetas): string | null {
  // De varias elegidas, el botón dice cuántas; los chips dicen cuáles.
  if (f.tipo !== 'fechas' && f.varias && listaDe(valores[f.clave]).length > 1) return String(listaDe(valores[f.clave]).length);
  return chipsActivos([f], valores)[0]?.texto.slice(f.titulo.length + 2) ?? null;
}

/** Las opciones de una faceta como lista de «elige una». En el panel del teléfono van como chips. */
function Opciones({ faceta, valor, enPanel, onElegir }: { faceta: FacetaDeOpciones; valor?: string; enPanel?: boolean; onElegir: (v: string | undefined) => void }) {
  const todas = [{ valor: '', etiqueta: faceta.todas ?? 'Todos', cuenta: undefined as number | undefined }, ...faceta.opciones];
  // En una faceta de `varias`, tocar una la pone o la quita; «Todas» las quita todas.
  const elegidas = faceta.varias ? listaDe(valor) : null;
  const esElegida = (v: string) => (elegidas ? (v === '' ? elegidas.length === 0 : elegidas.includes(v)) : (valor ?? '') === v);
  const elegir = (v: string) => onElegir(elegidas ? (v === '' ? undefined : alternarEnVarias(valor, v)) : v || undefined);
  if (enPanel) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {todas.map((o) => {
          const elegida = esElegida(o.valor);
          return (
            <button
              key={o.valor || '__todas'}
              type="button"
              aria-pressed={elegida}
              onClick={() => elegir(o.valor)}
              className={cn(
                'inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-3 text-sm transition-colors lateral:min-h-6 lateral:rounded-md lateral:px-2.5',
                FOCO,
                elegida
                  ? 'border-transparent bg-acento-lleno text-acento-sobre'
                  : 'border-transparent bg-[var(--mac-btn)] text-slate-900 active:bg-[var(--mac-btn-pulsado)] dark:text-slate-100',
              )}
            >
              {o.etiqueta}
              {o.cuenta != null && <span className={cn('text-xs tabular-nums', elegida ? 'text-white/80' : 'text-slate-500 dark:text-slate-400')}>{o.cuenta}</span>}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div role="menu" aria-label={faceta.titulo} className="flex flex-col">
      {todas.map((o) => {
        const elegida = esElegida(o.valor);
        return (
          <button
            key={o.valor || '__todas'}
            type="button"
            role={elegidas ? 'menuitemcheckbox' : 'menuitemradio'}
            aria-checked={elegida}
            onClick={() => elegir(o.valor)}
            className={cn(
              'group flex min-h-[44px] items-center gap-2 rounded-[7px] px-2 text-left text-sm hover:bg-acento-lleno hover:text-acento-sobre lateral:min-h-6',
              FOCO,
              elegida ? 'font-medium text-slate-900 dark:text-slate-100' : 'text-slate-900 dark:text-slate-100',
            )}
          >
            <Check {...ICONO} className={cn('h-4 w-4 shrink-0', !elegida && 'invisible')} />
            <span className="min-w-0 flex-1 truncate">{o.etiqueta}</span>
            {o.cuenta != null && <span className="shrink-0 text-xs tabular-nums text-slate-500 group-hover:text-white/80 dark:text-slate-400">{o.cuenta}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** El rango de fechas: atajos (hoy, 7 y 30 días) y los dos días a mano. */
function Fechas({ faceta, valores, onCambiar }: { faceta: FacetaDeFechas; valores: ValoresDeFacetas; onCambiar: (c: ValoresDeFacetas) => void }) {
  const desde = valores[faceta.desde] ?? '';
  const hasta = valores[faceta.hasta] ?? '';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {atajosDeFecha().map((a) => {
          const elegido = desde === a.desde && hasta === a.hasta;
          return (
            <button
              key={a.etiqueta}
              type="button"
              aria-pressed={elegido}
              onClick={() => onCambiar({ [faceta.desde]: a.desde, [faceta.hasta]: a.hasta })}
              className={cn(
                'min-h-[44px] rounded-full border px-3 text-sm transition-colors lateral:min-h-6 lateral:rounded-md lateral:px-2.5',
                FOCO,
                elegido
                  ? 'border-transparent bg-acento-lleno text-acento-sobre'
                  : 'border-transparent bg-[var(--mac-btn)] text-slate-900 active:bg-[var(--mac-btn-pulsado)] dark:text-slate-100',
              )}
            >
              {a.etiqueta}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {([['desde', 'Desde', desde], ['hasta', 'Hasta', hasta]] as const).map(([campo, rotulo, valor]) => (
          <label key={campo} className="flex flex-col gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
            {rotulo}
            <input
              type="date"
              value={valor}
              max={faceta.hastaHoy ? diaLocal(new Date()) : undefined}
              onChange={(e) => onCambiar({ [faceta[campo]]: e.target.value || undefined })}
              className="h-11 w-full rounded-[10px] border-0 bg-surface px-2 text-base text-slate-900 shadow-[0_0_0_1px_var(--mac-campo-filo)] focus:outline-none focus:ring-[3.5px] focus:ring-acento/50 lateral:h-6 lateral:rounded-[5px] lateral:text-sm dark:text-slate-100"
            />
          </label>
        ))}
      </div>
      {(desde || hasta) && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {desde && hasta ? `Del ${fechaCorta(desde)} al ${fechaCorta(hasta)}, los dos incluidos.` : desde ? `Desde el ${fechaCorta(desde)}.` : `Hasta el ${fechaCorta(hasta)}.`}
        </p>
      )}
    </div>
  );
}

function ContenidoDeFaceta({ faceta, valores, enPanel, onCambiar, onListo }: { faceta: Faceta; valores: ValoresDeFacetas; enPanel?: boolean; onCambiar: (c: ValoresDeFacetas) => void; onListo?: () => void }) {
  if (faceta.tipo === 'fechas') return <Fechas faceta={faceta} valores={valores} onCambiar={onCambiar} />;
  return (
    <Opciones
      faceta={faceta}
      valor={valores[faceta.clave]}
      enPanel={enPanel}
      onElegir={(v) => {
        onCambiar({ [faceta.clave]: v });
        // De varias se sigue eligiendo: no se cierra.
        if (!faceta.varias) onListo?.();
      }}
    />
  );
}

export function Superfiltro({
  buscar,
  onBuscar,
  placeholder = 'Buscar…',
  facetas: todasLasFacetas,
  valores,
  onCambiar,
  onLimpiar,
  resultados,
  acciones,
  chipsExtra = [],
  compacta = false,
  abiertaDeEntrada = null,
}: SuperfiltroProps) {
  const [abierta, setAbierta] = useState<string | null>(abiertaDeEntrada);
  const [aLaDerecha, setALaDerecha] = useState(false);
  // Una faceta sin opciones (nadie delegado todavía) no se dibuja, salvo que traiga un valor puesto.
  const facetas = todasLasFacetas.filter((f) => f.tipo === 'fechas' || f.opciones.length > 0 || Boolean(valores[f.clave]));
  const chips = chipsActivos(facetas, valores);
  const puestos = chips.length + chipsExtra.length;
  const hayAlgo = puestos > 0 || Boolean(buscar);

  useEffect(() => {
    if (!abierta) return;
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && setAbierta(null);
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierta]);

  const cerrar = () => setAbierta(null);
  const alternar = (cual: string) => setAbierta((a) => (a === cual ? null : cual));

  return (
    <section aria-label="Filtros de la lista" className="relative space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {onBuscar && (
          <div className={cn('relative min-w-[10rem] flex-1', compacta && 'basis-full')}>
            <Search {...ICONO} className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              data-tabla-buscador
              value={buscar ?? ''}
              onChange={(e) => onBuscar(e.target.value)}
              aria-label="Buscar"
              placeholder={placeholder}
              className={cn(
                // Campo de búsqueda de macOS 27: cápsula gris, lupa a la izquierda.
                'h-11 w-full rounded-full border-0 bg-[var(--mac-btn)] pl-9 pr-9 text-base text-slate-900 caret-acento placeholder:text-slate-500 lateral:h-8 lateral:text-sm',
                'focus:outline-none focus:ring-[3.5px] focus:ring-acento/50 dark:text-slate-100 dark:placeholder:text-slate-400',
                '[&::-webkit-search-cancel-button]:hidden',
              )}
            />
            {buscar && (
              <button
                type="button"
                aria-label="Borrar la búsqueda"
                onClick={() => onBuscar('')}
                className={cn('absolute right-0 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full text-slate-400 lateral:h-8 lateral:w-8 hover:text-slate-700 dark:hover:text-slate-200', FOCO)}
              >
                <X {...ICONO} className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* En escritorio, un desplegable por faceta. */}
        {!compacta &&
          facetas.map((f) => {
            const puesta = etiquetaPuesta(f, valores);
            const abiertaEsta = abierta === f.clave;
            return (
              <div key={f.clave} className="relative hidden lateral:block">
                <button
                  type="button"
                  aria-expanded={abiertaEsta}
                  aria-haspopup="menu"
                  data-activo={puesta ? 'true' : 'false'}
                  onClick={(e) => {
                    // Cerca del borde derecho, la caja (288 px) se abre hacia la izquierda: no se corta.
                    setALaDerecha(e.currentTarget.getBoundingClientRect().left + 288 > window.innerWidth - 16);
                    alternar(f.clave);
                  }}
                  className={cn(
                    'inline-flex h-7 max-w-[16rem] items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors',
                    FOCO,
                    puesta
                      ? 'border-transparent bg-[var(--mac-tintado)] text-acento-texto active:bg-[var(--mac-tintado-pulsado)]'
                      : 'border-transparent bg-[var(--mac-btn)] text-slate-900 active:bg-[var(--mac-btn-pulsado)] dark:text-slate-100',
                  )}
                >
                  <span className="truncate">
                    {f.titulo}
                    {puesta && <span className="font-medium">: {puesta}</span>}
                  </span>
                  <ChevronDown {...ICONO} className={cn('h-4 w-4 shrink-0 opacity-60 transition-transform motion-reduce:transition-none', abiertaEsta && 'rotate-180')} />
                </button>
                {abiertaEsta && (
                  <>
                    <button type="button" aria-label="Cerrar" tabIndex={-1} onClick={cerrar} className="fixed inset-0 z-30 cursor-default" />
                    <div className={cn('absolute top-full z-40 mt-1', aLaDerecha ? 'right-0' : 'left-0', 'mac-menu max-h-80 w-72 overflow-y-auto motion-safe:animate-scale-in')}>
                      <p className="px-2 pb-1 pt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">{f.titulo}</p>
                      <ContenidoDeFaceta faceta={f} valores={valores} onCambiar={onCambiar} onListo={cerrar} />
                    </div>
                  </>
                )}
              </div>
            );
          })}

        {/* En el teléfono (o en una columna angosta), un solo botón con todo. */}
        {facetas.length > 0 && (
          <button
            type="button"
            aria-expanded={abierta === PANEL}
            aria-haspopup="dialog"
            data-activo={puestos > 0 ? 'true' : 'false'}
            onClick={() => alternar(PANEL)}
            className={cn(
              'relative inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg border px-3 text-sm transition-colors lateral:h-7 lateral:rounded-md lateral:px-2.5',
              FOCO,
              !compacta && 'lateral:hidden',
              puestos > 0
                ? 'border-transparent bg-[var(--mac-tintado)] text-acento-texto'
                : 'border-transparent bg-[var(--mac-btn)] text-slate-900 active:bg-[var(--mac-btn-pulsado)] dark:text-slate-100',
            )}
          >
            <SlidersHorizontal {...ICONO} className="h-4 w-4" />
            Filtros
            {puestos > 0 && <span className="rounded-full bg-acento-lleno px-1.5 text-xs tabular-nums text-acento-sobre">{puestos}</span>}
          </button>
        )}

        {acciones && <div className="ml-auto flex shrink-0 items-center gap-1">{acciones}</div>}
      </div>

      {/* Cuántos hay, lo puesto y «Limpiar». */}
      {(resultados || hayAlgo) && (
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          {resultados && (
            <span aria-live="polite" className="mr-1 tabular-nums text-slate-500 dark:text-slate-400">
              {resultados}
            </span>
          )}
          {chips.map((c) => (
            <span key={c.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-[var(--mac-tintado)] py-0.5 pl-2.5 pr-0.5 text-xs text-acento-texto">
              <span className="truncate">{c.texto}</span>
              <button type="button" aria-label={`Quitar ${c.texto}`} onClick={() => onCambiar(c.quitar)} className={cn('grid h-7 w-7 place-items-center rounded-full hover:bg-[var(--mac-tintado-pulsado)]', FOCO)}>
                <X {...ICONO} className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          {chipsExtra.map((c) => (
            <span key={c.clave} className="inline-flex max-w-full items-center gap-1 rounded-full bg-[var(--mac-tintado)] py-0.5 pl-2.5 pr-0.5 text-xs text-acento-texto">
              <span className="truncate">{c.etiqueta}</span>
              <button type="button" aria-label={`Quitar ${c.etiqueta}`} onClick={c.onQuitar} className={cn('grid h-7 w-7 place-items-center rounded-full hover:bg-[var(--mac-tintado-pulsado)]', FOCO)}>
                <X {...ICONO} className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          {hayAlgo && onLimpiar && (
            <button type="button" onClick={onLimpiar} className={cn('min-h-[32px] rounded px-1 text-xs text-acento-texto hover:underline', FOCO)}>
              Limpiar
            </button>
          )}
        </div>
      )}

      {/* La hoja del teléfono: todas las facetas juntas. En `compacta` y escritorio, una caja debajo de la barra. */}
      {abierta === PANEL && (
        <>
          <button type="button" aria-label="Cerrar" tabIndex={-1} onClick={cerrar} className="fixed inset-0 z-40 !mt-0 cursor-default bg-[var(--mac-velo)] motion-safe:animate-fade-in lateral:bg-transparent" />
          <div
            role="dialog"
            aria-label="Filtros"
            className={cn(
              'mac-hoja fixed inset-x-0 bottom-0 z-50 !mt-0 flex max-h-[85dvh] flex-col rounded-t-3xl motion-safe:animate-subir',
              'lateral:absolute lateral:bottom-auto lateral:top-full lateral:mt-1 lateral:max-h-[70vh] lateral:rounded-lg lateral:motion-safe:animate-scale-in',
            )}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-surface-border py-1 pl-4 pr-1">
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Filtros</h2>
              <button type="button" aria-label="Cerrar los filtros" onClick={cerrar} className={cn('grid h-11 w-11 place-items-center rounded-md text-slate-500 hover:text-slate-800 dark:hover:text-slate-100', FOCO)}>
                <X {...ICONO} className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-4">
              {facetas.map((f) => (
                <fieldset key={f.clave} className="space-y-2">
                  <legend className="text-sm font-medium text-slate-700 dark:text-slate-200">{f.titulo}</legend>
                  <ContenidoDeFaceta faceta={f} valores={valores} enPanel onCambiar={onCambiar} />
                </fieldset>
              ))}
            </div>
            <div className="flex shrink-0 items-center gap-3 border-t border-surface-border px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
              <button type="button" disabled={!hayAlgo || !onLimpiar} onClick={onLimpiar} className={cn('min-h-[44px] rounded px-2 text-sm text-acento-texto hover:underline disabled:text-slate-400 disabled:no-underline', FOCO)}>
                Limpiar
              </button>
              <button type="button" onClick={cerrar} className={cn('ml-auto min-h-[44px] rounded-lg bg-acento-lleno px-4 text-sm text-acento-sobre active:bg-[var(--acento-pulsado)] lateral:min-h-7 lateral:rounded-md', FOCO)}>
                {resultados ? `Ver ${resultados}` : 'Listo'}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
